import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import API_URL from '../../config';

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  
  const [formData, setFormData] = useState({
    orderId: '',
    email: '',
    name: '',
    items: '',
    carrier: '',
    trackingNumber: '',
    trackingUrl: '',
    estimatedDelivery: '',
  });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  // Simple password check - change this password!
  const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD;


  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setPasswordError(false);
    } else {
      setPasswordError(true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    try {
      // Parse items (format: "Product Name, Size, Qty, ImageURL" per line)
      const items = formData.items.split('\n').filter(line => line.trim()).map(line => {
        const [name, size, quantity, image] = line.split(',').map(s => s.trim());
        return {
          name,
          size: size || null,
          quantity: parseInt(quantity) || 1,
          image: image || '',
        };
      });

      const response = await fetch(`${API_URL}/api/send-dispatch-email`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Admin-Key': process.env.REACT_APP_ADMIN_SECRET,
        },
        body: JSON.stringify({
          orderId: formData.orderId,
          email: formData.email,
          name: formData.name,
          items,
          trackingInfo: {
            carrier: formData.carrier,
            trackingNumber: formData.trackingNumber,
            trackingUrl: formData.trackingUrl,
            estimatedDelivery: formData.estimatedDelivery,
          },
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus({ type: 'success', message: 'Dispatch email sent successfully!' });
        setFormData({
          orderId: '',
          email: '',
          name: '',
          items: '',
          carrier: '',
          trackingNumber: '',
          trackingUrl: '',
          estimatedDelivery: '',
        });
      } else {
        throw new Error(data.error || 'Failed to send email');
      }
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: '#1C1C1C',
    border: '1px solid #333',
    borderRadius: '4px',
    color: '#F1F1F1',
    fontSize: '14px',
    fontFamily: "'TASA Orbiter Display', sans-serif",
    outline: 'none',
    marginBottom: '16px',
  };

  const labelStyle = {
    display: 'block',
    color: '#8A8A8A',
    fontSize: '12px',
    marginBottom: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0D0D0D',
        fontFamily: "'TASA Orbiter Display', sans-serif",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Helmet>
          <title>Admin | RTK</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div style={{
          backgroundColor: '#1C1C1C',
          borderRadius: '8px',
          padding: '40px',
          width: '100%',
          maxWidth: '360px',
        }}>
          <h1 style={{ color: '#F1F1F1', fontSize: '18px', margin: '0 0 24px 0', textAlign: 'center' }}>
            Admin Access
          </h1>
          
          <form onSubmit={handleLogin}>
            <input
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{
                ...inputStyle,
                marginBottom: passwordError ? '8px' : '16px',
              }}
            />
            
            {passwordError && (
              <p style={{ color: '#E63230', fontSize: '13px', margin: '0 0 16px 0' }}>
                Incorrect password
              </p>
            )}
            
            <button
              type="submit"
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: '#F1F1F1',
                color: '#0D0D0D',
                border: 'none',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                fontFamily: "'TASA Orbiter Display', sans-serif",
              }}
            >
              Login
            </button>
          </form>
          
          <Link 
            to="/" 
            style={{ 
              display: 'block', 
              textAlign: 'center', 
              marginTop: '24px', 
              color: '#8A8A8A', 
              fontSize: '13px',
              textDecoration: 'none',
            }}
          >
            ← Back to Store
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0D0D0D',
      fontFamily: "'TASA Orbiter Display', sans-serif",
    }}>
      <Helmet>
        <title>Admin | RTK</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      {/* Header */}
      <header style={{
        height: '70px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 20px',
        borderBottom: '1px solid #1C1C1C',
      }}>
        <Link to="/" style={{ color: '#8A8A8A', textDecoration: 'none', fontSize: '14px' }}>
          ← Back to Store
        </Link>
        <h1 style={{ color: '#F1F1F1', fontSize: '16px', margin: 0 }}>Admin - Send Dispatch Email</h1>
        <div style={{ width: '100px' }}></div>
      </header>

      {/* Main Content */}
      <main style={{
        maxWidth: '600px',
        margin: '0 auto',
        padding: '40px 20px',
      }}>
        <form onSubmit={handleSubmit}>
          {/* Order Details Section */}
          <div style={{
            backgroundColor: '#1C1C1C',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '24px',
          }}>
            <h2 style={{ color: '#F1F1F1', fontSize: '14px', margin: '0 0 20px 0' }}>
              Order Details
            </h2>

            <label style={labelStyle}>Order ID</label>
            <input
              type="text"
              name="orderId"
              placeholder="pi_xxxxxxxxxxxxx"
              value={formData.orderId}
              onChange={handleInputChange}
              required
              style={inputStyle}
            />

            <label style={labelStyle}>Customer Email</label>
            <input
              type="email"
              name="email"
              placeholder="customer@email.com"
              value={formData.email}
              onChange={handleInputChange}
              required
              style={inputStyle}
            />

            <label style={labelStyle}>Customer Name</label>
            <input
              type="text"
              name="name"
              placeholder="John Doe"
              value={formData.name}
              onChange={handleInputChange}
              required
              style={inputStyle}
            />

            <label style={labelStyle}>Items (one per line: Name, Size, Qty, ImageURL)</label>
            <textarea
              name="items"
              placeholder="Black T-Shirt, M, 1, https://example.com/image.jpg
Art Print, A3, 2, https://example.com/print.jpg"
              value={formData.items}
              onChange={handleInputChange}
              required
              rows={4}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          {/* Tracking Info Section */}
          <div style={{
            backgroundColor: '#1C1C1C',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '24px',
          }}>
            <h2 style={{ color: '#F1F1F1', fontSize: '14px', margin: '0 0 20px 0' }}>
              Tracking Information
            </h2>

            <label style={labelStyle}>Carrier</label>
            <input
              type="text"
              name="carrier"
              placeholder="Royal Mail, DHL, FedEx, etc."
              value={formData.carrier}
              onChange={handleInputChange}
              style={inputStyle}
            />

            <label style={labelStyle}>Tracking Number</label>
            <input
              type="text"
              name="trackingNumber"
              placeholder="ABC123456789"
              value={formData.trackingNumber}
              onChange={handleInputChange}
              style={inputStyle}
            />

            <label style={labelStyle}>Tracking URL</label>
            <input
              type="url"
              name="trackingUrl"
              placeholder="https://track.carrier.com/ABC123456789"
              value={formData.trackingUrl}
              onChange={handleInputChange}
              style={inputStyle}
            />

            <label style={labelStyle}>Estimated Delivery</label>
            <input
              type="text"
              name="estimatedDelivery"
              placeholder="5-7 business days"
              value={formData.estimatedDelivery}
              onChange={handleInputChange}
              style={inputStyle}
            />
          </div>

          {/* Status Message */}
          {status && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: status.type === 'success' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(230, 50, 48, 0.1)',
              border: `1px solid ${status.type === 'success' ? '#4CAF50' : '#E63230'}`,
              borderRadius: '4px',
              marginBottom: '16px',
              color: status.type === 'success' ? '#4CAF50' : '#E63230',
              fontSize: '14px',
            }}>
              {status.message}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '16px',
              backgroundColor: loading ? '#333' : '#F1F1F1',
              color: loading ? '#8A8A8A' : '#0D0D0D',
              border: 'none',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: "'TASA Orbiter Display', sans-serif",
            }}
          >
            {loading ? 'Sending...' : '📦 Send Dispatch Email'}
          </button>
        </form>
      </main>
    </div>
  );
};

export default Admin;